import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CreditCard,
  LineChart,
  Wallet,
} from "lucide-react";

import {
  AllocationChart,
  type AllocationSegment,
} from "@/components/dashboard/allocation-chart";
import { LastUpdated } from "@/components/data/last-updated";
import { RefreshButton } from "@/components/data/refresh-button";
import { SourceBadge } from "@/components/data/source-badge";
import { SyncStatusPill } from "@/components/data/sync-status-pill";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  fetchRecentRecords,
  fetchSummary,
  refreshResourceForCategory,
  type RecentRecord,
} from "@/lib/api";
import {
  formatAbsoluteDateTime,
  formatCurrency,
  formatCurrencyCompact,
  formatRelativeTime,
} from "@/lib/format";
import { cn } from "@/lib/utils";
import type { Summary } from "@finance/shared";

interface DashboardState {
  status: "loading" | "ready" | "error";
  summary: Summary | null;
  recent: RecentRecord[];
  error: string | null;
}

const INITIAL: DashboardState = {
  status: "loading",
  summary: null,
  recent: [],
  error: null,
};

export function DashboardPage() {
  const [state, setState] = useState<DashboardState>(INITIAL);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const [summary, recent] = await Promise.all([
        fetchSummary(signal),
        fetchRecentRecords(5, signal),
      ]);
      if (signal?.aborted) return;
      setState({
        status: "ready",
        summary,
        recent,
        error: null,
      });
    } catch (err) {
      if (signal?.aborted) return;
      setState({
        status: "error",
        summary: null,
        recent: [],
        error: err instanceof Error ? err.message : "Failed to load dashboard",
      });
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    setState(INITIAL);
    void load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Overview"
        title="Dashboard"
        description="A calm, end-of-day view of your assets, liabilities, and net worth."
      />

      {state.status === "loading" ? <DashboardSkeleton /> : null}

      {state.status === "error" ? (
        <Card className="border-destructive/40">
          <CardContent className="flex items-start gap-3 py-6">
            <AlertTriangle
              className="mt-0.5 h-4 w-4 shrink-0 text-destructive"
              aria-hidden
            />
            <div className="space-y-1 text-sm">
              <p className="font-medium text-foreground">
                Couldn&apos;t load the dashboard.
              </p>
              <p className="font-mono text-xs text-muted-foreground">
                {state.error}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {state.status === "ready" && state.summary ? (
        <DashboardContent
          summary={state.summary}
          recent={state.recent}
          onRefreshed={() => void load()}
        />
      ) : null}
    </div>
  );
}

interface DashboardContentProps {
  summary: Summary;
  recent: RecentRecord[];
  onRefreshed: () => void;
}

function DashboardContent({ summary, recent, onRefreshed }: DashboardContentProps) {
  const hasData =
    summary.total_assets > 0 ||
    summary.total_liabilities > 0 ||
    recent.length > 0;

  if (!hasData) {
    return <EmptyState />;
  }

  const allocation: AllocationSegment[] = [
    {
      key: "cash",
      label: "Cash",
      value: summary.breakdown_by_category.bank_accounts,
      color: "hsl(var(--accent))",
    },
    {
      key: "investments",
      label: "Investments",
      value: summary.breakdown_by_category.investments,
      color: "hsl(var(--accent) / 0.7)",
    },
  ];

  return (
    <>
      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Total assets" value={formatCurrency(summary.total_assets)} />
        <KpiCard
          label="Total liabilities"
          value={formatCurrency(summary.total_liabilities)}
        />
        <KpiCard
          label="Net worth"
          value={formatCurrency(summary.net_worth)}
          accent
        />
        <KpiCard
          label="Last updated"
          value={
            summary.last_updated_at
              ? formatRelativeTime(summary.last_updated_at)
              : "—"
          }
          hint={
            summary.last_updated_at
              ? formatAbsoluteDateTime(summary.last_updated_at)
              : "No records yet"
          }
          mono={false}
        />
      </section>

      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Cash balance" value={formatCurrency(summary.cash_balance)} />
        <KpiCard
          label="Credit card owed"
          value={formatCurrency(summary.credit_card_owed)}
        />
        <CashNetOfCreditCard
          cashBalance={summary.cash_balance}
          creditCardOwed={summary.credit_card_owed}
        />
        <KpiCard
          label="Investments total"
          value={formatCurrency(summary.investments_total)}
        />
      </section>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <Card className="lg:col-span-3">
          <CardHeader className="flex flex-row items-start justify-between space-y-0">
            <div>
              <CardTitle>Recent updates</CardTitle>
              <p className="mt-1 text-xs text-muted-foreground">
                The five most recently updated records across all categories.
              </p>
            </div>
            <Badge variant="outline" className="font-mono text-[10px] uppercase">
              {recent.length} of 5
            </Badge>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            {recent.length === 0 ? (
              <p className="px-6 pb-6 text-sm text-muted-foreground">
                No records updated yet.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="pl-6">Record</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Sync</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="w-12 pr-6" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recent.map((row) => (
                    <TableRow key={`${row.category}:${row.id}`}>
                      <TableCell className="pl-6">
                        <div className="font-medium text-foreground">{row.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {row.subtitle}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {row.categoryLabel}
                      </TableCell>
                      <TableCell>
                        <SourceBadge source={row.source} />
                      </TableCell>
                      <TableCell>
                        <SyncStatusPill
                          source={row.source}
                          lastSyncedAt={row.last_synced_at}
                          lastSyncStatus={row.last_sync_status}
                        />
                      </TableCell>
                      <TableCell>
                        <LastUpdated
                          iso={row.last_updated_at}
                          className="font-mono text-xs"
                        />
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-right font-mono tabular-nums",
                          row.amountKind === "liability"
                            ? "text-destructive"
                            : "text-foreground",
                        )}
                      >
                        {row.amountKind === "liability" ? "−" : ""}
                        {formatCurrency(row.amount)}
                      </TableCell>
                      <TableCell className="pr-6 text-right">
                        {row.source === "api" ? (
                          <RefreshButton
                            resource={refreshResourceForCategory(row.category)}
                            id={row.id}
                            label={row.name}
                            onRefreshed={onRefreshed}
                          />
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Allocation</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Share of assets by category. Liabilities are shown separately.
            </p>
          </CardHeader>
          <CardContent>
            <AllocationChart
              segments={allocation}
              centerLabel="Assets"
              centerValue={formatCurrencyCompact(summary.total_assets)}
              liabilities={
                summary.total_liabilities > 0
                  ? { label: "Liabilities", value: summary.total_liabilities }
                  : null
              }
            />
          </CardContent>
        </Card>
      </section>
    </>
  );
}

interface KpiCardProps {
  label: string;
  value: string;
  hint?: string;
  accent?: boolean;
  mono?: boolean;
}

function KpiCard({ label, value, hint, accent, mono = true }: KpiCardProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={cn(
            "text-2xl font-semibold tracking-tight",
            mono && "font-mono tabular-nums",
            accent && "text-accent",
          )}
        >
          {value}
        </div>
        {hint ? (
          <p className="mt-1 font-mono text-[11px] text-muted-foreground">{hint}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

interface CashNetOfCreditCardProps {
  cashBalance: number;
  creditCardOwed: number;
}

function CashNetOfCreditCard({
  cashBalance,
  creditCardOwed,
}: CashNetOfCreditCardProps) {
  const net = cashBalance - creditCardOwed;
  const isNegative = net < 0;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>Cash net of credit</CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={cn(
            "text-2xl font-semibold tracking-tight font-mono tabular-nums",
            isNegative ? "text-destructive" : "text-foreground",
          )}
        >
          {isNegative ? "−" : ""}
          {formatCurrency(Math.abs(net))}
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Cash balance minus credit card owed
        </p>
      </CardContent>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <div className="h-3 w-20 animate-pulse rounded bg-muted" />
            </CardHeader>
            <CardContent>
              <div className="h-7 w-28 animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
        ))}
      </section>
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <Card className="lg:col-span-3">
          <CardContent className="space-y-3 py-6">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-6 w-full animate-pulse rounded bg-muted" />
            ))}
          </CardContent>
        </Card>
        <Card className="lg:col-span-2">
          <CardContent className="flex items-center justify-center py-10">
            <div className="h-40 w-40 animate-pulse rounded-full bg-muted" />
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

const EMPTY_LINKS: Array<{
  to: string;
  label: string;
  description: string;
  Icon: typeof Wallet;
}> = [
  {
    to: "/accounts",
    label: "Bank accounts",
    description: "Checking, savings, and CDs",
    Icon: Wallet,
  },
  {
    to: "/credit-cards",
    label: "Credit cards",
    description: "Balances and utilization",
    Icon: CreditCard,
  },
  {
    to: "/investments",
    label: "Investments",
    description: "Stocks, ETFs, and funds",
    Icon: LineChart,
  },
];

function EmptyState() {
  return (
    <Card>
      <CardContent className="space-y-6 py-10">
        <div className="space-y-1 text-center">
          <p className="text-sm font-medium text-foreground">Get started</p>
          <p className="mx-auto max-w-md text-sm text-muted-foreground">
            Add your first record to start tracking net worth. You can pick any
            category to begin — the dashboard updates as soon as records exist.
          </p>
        </div>
        <ul className="mx-auto grid w-full max-w-2xl grid-cols-1 gap-2 sm:grid-cols-2">
          {EMPTY_LINKS.map(({ to, label, description, Icon }) => (
            <li key={to}>
              <Link
                to={to}
                className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2.5 text-sm transition-colors hover:bg-muted"
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-border bg-background text-muted-foreground">
                  <Icon className="h-4 w-4" aria-hidden />
                </span>
                <span className="flex flex-col">
                  <span className="font-medium text-foreground">{label}</span>
                  <span className="text-xs text-muted-foreground">{description}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
